import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, AcademicCapIcon, ChartBarIcon, LightBulbIcon, CogIcon } from '@heroicons/react/24/outline';

const AIPoweredLearning = () => {
  const navigate = useNavigate();

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const features = [
    {
      icon: AcademicCapIcon,
      title: "Personalized Study Plans",
      description: "AI analyzes your performance and creates customized study schedules that adapt to your learning pace and weak areas."
    },
    {
      icon: ChartBarIcon,
      title: "Performance Analytics",
      description: "Advanced analytics track your progress across subjects, identifying patterns and suggesting optimal study strategies."
    },
    {
      icon: LightBulbIcon,
      title: "Smart Recommendations",
      description: "Get intelligent suggestions for what to study next based on your current performance and exam timeline."
    },
    {
      icon: CogIcon,
      title: "Adaptive Difficulty",
      description: "Questions automatically adjust difficulty based on your performance, ensuring optimal learning progression."
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <nav className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <button
                onClick={() => navigate(-1)}
                className="flex items-center text-gray-600 hover:text-blue-600"
              >
                <ArrowLeftIcon className="h-5 w-5 mr-2" />
                Back
              </button>
            </div>
            <div className="flex-shrink-0">
              <h1 className="text-2xl font-bold text-blue-600">W3Skills</h1>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-50 to-orange-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="w-20 h-20 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-6">
              <AcademicCapIcon className="h-10 w-10 text-white" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              AI-Powered Learning
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Experience the future of CET preparation with intelligent algorithms that adapt to your learning style 
              and optimize your study journey for maximum success.
            </p>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              How AI Enhances Your Learning
            </h2>
            <p className="text-xl text-gray-600">
              Our advanced AI system works behind the scenes to make your CET preparation more effective and efficient.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="bg-gray-50 p-8 rounded-lg">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-6">
                  <feature.icon className="h-6 w-6 text-blue-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              How It Works
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-white font-bold text-xl">1</span>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Analyze Performance</h3>
              <p className="text-gray-600">AI continuously monitors your quiz results, response times, and subject mastery.</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-white font-bold text-xl">2</span>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Generate Insights</h3>
              <p className="text-gray-600">Advanced algorithms identify your weak areas and learning patterns.</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-white font-bold text-xl">3</span>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Adapt & Optimize</h3>
              <p className="text-gray-600">Study plans and question difficulty automatically adjust to maximize your learning.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-blue-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Ready to Experience AI-Powered Learning?
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Join thousands of students who are already benefiting from our intelligent learning system.
          </p>
          <button
            onClick={() => navigate('/login')}
            className="bg-white text-blue-600 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-colors"
          >
            Start Learning Now
          </button>
        </div>
      </section>
    </div>
  );
};

export default AIPoweredLearning;
